import { Injectable } from "@angular/core";
import { UserActivityLog } from "../models/user-activity-log.model";
import { Timestamp } from "@firebase/firestore";
import { TenantService } from "./tenant.service";
import { MessagesService } from "../shared/services/messages.service";
import { EventType } from "@angular/router";
import { UserActivityEvent } from "../models/user-activity-event.model";
import { createFirestoreId } from "../common/firestore-utils";
import { Firestore, QueryConstraint, collection, collectionData, doc, getDoc, query, setDoc, updateDoc, where } from "@angular/fire/firestore";
import { first } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class UserActivityLogService {
  constructor(
    private tenant: TenantService,
    private messages: MessagesService,
    private firestore: Firestore
  ) {}

  async loadAllUserActivityLog(userId: string) {
    try {

      const constraints: QueryConstraint[] = [
        where("userId", "==", userId)
      ]

      const allUserActivityLogQuery = query(collection(this.firestore, `schools/${this.tenant.id}/userActivityLog`), ...constraints)

      const userActivityLogData = await collectionData(allUserActivityLogQuery).pipe(first()).toPromise()

      return userActivityLogData as UserActivityLog[]

    } catch (error) {
      const message = `Error loading all user activity for userId=${userId}`;
      console.error(message, error);
      this.messages.error(message);
    }
  }

  async loadUserActivityLogForCourse(userId: string, courseId?: string) {
    try {

      const constraints: QueryConstraint[] = [
        where("userId", "==", userId),
        where("courseId", "==", courseId)
      ];

      const loadUserActivityLogForCourseQuery = query(
        collection(this.firestore, `schools/${this.tenant.id}/userActivityLog`),
        ...constraints
      )

      const loadUserActivityLogForCourseData = await collectionData(loadUserActivityLogForCourseQuery).pipe(first()).toPromise();

      return loadUserActivityLogForCourseData as UserActivityLog[];

    } catch (error) {
      const message = `Error loading user activity for userId=${userId}`;
      console.error(message, error);
      this.messages.error(message);
    }
  }

  async reportCourseCertificateObtained(userId: string, courseId: string) {
    return await this.reportCourseEvent(
      userId,
      courseId,
      "course-certificate-obtained"
    );
  }

  async reportCourseEnrollment(userId: string, courseId: string) {
    return await this.reportCourseEvent(userId, courseId, "course-enrollment");
  }

  async reportCourseUnEnrollment(userId: string, courseId: string) {
    return await this.reportCourseEvent(
      userId,
      courseId,
      "course-unenrollment"
    );
  }

  async reportCourseStarted(userId: string, courseId: string) {
    return await this.reportCourseEvent(userId, courseId, "course-started");
  }

  private async reportCourseEvent(
    userId: string,
    courseId: string,
    eventType: UserActivityEvent
  ) {
    try {

      const constraints: QueryConstraint[] = [
        where("userId", "==", userId),
        where("courseId", "==", courseId),
        where("eventType", "==", eventType)
      ]

      const loadUserActivityLogForCourseEventTypeQuery = query(
        collection(this.firestore, `schools/${this.tenant.id}/userActivityLog`),
        ...constraints
      )

      const loadUserActivityLogForCourseEventTypeData = await collectionData(loadUserActivityLogForCourseEventTypeQuery).pipe(first()).toPromise();

      console.log("reportCourseEvent", loadUserActivityLogForCourseEventTypeData, userId, courseId, eventType)

      if(loadUserActivityLogForCourseEventTypeData.length == 0) {
        const userActivityId = createFirestoreId(this.firestore)
        const path = `schools/${this.tenant.id}/userActivityLog/${userActivityId}`;
        const collectionRef = doc(this.firestore, path);

        const data: UserActivityLog = {
          id: userActivityId,
          createdAt: Timestamp.now(),
          eventType,
          userId,
          courseId,
        };

        await setDoc(collectionRef, data);

        return data;
      }

      return loadUserActivityLogForCourseEventTypeData[0] as UserActivityLog;

    } catch (error) {
      const message = `Error reporting a course event of type ${eventType}: courseId=${courseId} userId=${userId}`;
      console.error(message, error);
      this.messages.error(message);
    }
  }

  async reportUserSignup(userId: string, tenantId?: string) {
    return await this.reportEvent(userId, null, "user-signup", {}, tenantId);
  }

  async reportLastSignin(userId: string, tenantId?: string) {
    return await this.reportEvent(userId, null, "last-signin", {}, tenantId);
  }

  async reportUserLogout(userId: string, tenantId?: string) {
    return await this.reportEvent(userId, null, "user-logout", {}, tenantId);
  }

  async reportCourseVisit(userId: string, courseId: string) {
    // Only log one visit per day per course per user
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      const constraints: QueryConstraint[] = [
        where("userId", "==", userId),
        where("courseId", "==", courseId),
        where("eventType", "==", "course-visit"),
        where("createdAt", ">=", Timestamp.fromDate(today)),
        where("createdAt", "<", Timestamp.fromDate(tomorrow))
      ];

      const visitQuery = query(
        collection(this.firestore, `schools/${this.tenant.id}/userActivityLog`),
        ...constraints
      );

      const visitsToday = await collectionData(visitQuery).pipe(first()).toPromise();

      if (!visitsToday.length) {
        return await this.reportEvent(userId, courseId, "course-visit");
      } else {
        // Optionally update timestamp to latest
        const visitDoc = doc(this.firestore, `schools/${this.tenant.id}/userActivityLog/${visitsToday[0].id}`);
        await updateDoc(visitDoc, { createdAt: Timestamp.now() });
        return visitsToday[0];
      }

      // return visitsToday[0];
    } catch (error) {
      const message = `Error reporting course visit: courseId=${courseId} userId=${userId}`;
      console.error(message, error);
      this.messages.error(message);
    }
  }

  async reportCourseCompleted(userId: string, courseId: string) {
    return await this.reportEvent(userId, courseId, "course-completed");
  }

  // Uncomment this method if you want to report the last activity everytime a user views a lesson. not once per day.
  // async reportLastActivity(userId: string, courseId: string, lessonId: string) {
  //   // Save the last lesson viewed as metadata
  //   return await this.reportEvent(userId, courseId, "last-activity", { lessonId });
  // }

  async reportLastActivity(userId: string, courseId: string, lessonId: string) {
    // Save the last lesson viewed as metadata
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      const constraints: QueryConstraint[] = [
        where("userId", "==", userId),
        where("courseId", "==", courseId),
        where("eventType", "==", "last-activity"),
        where("createdAt", ">=", Timestamp.fromDate(today)),
        where("createdAt", "<", Timestamp.fromDate(tomorrow))
      ];

      const visitQuery = query(
        collection(this.firestore, `schools/${this.tenant.id}/userActivityLog`),
        ...constraints
      );

      const visitsToday = await collectionData(visitQuery).pipe(first()).toPromise();

      if (!visitsToday.length) {
        return await this.reportEvent(userId, courseId, "last-activity", { lessonId });
      } else {
        // Optionally update timestamp to latest
        const visitDoc = doc(this.firestore, `schools/${this.tenant.id}/userActivityLog/${visitsToday[0].id}`);
        await updateDoc(visitDoc, {
          createdAt: Timestamp.now(),
          lessonId
        });
        return visitsToday[0];
      }

      // return visitsToday[0];
    } catch (error) {
      const message = `Error reporting last activity: courseId=${courseId} userId=${userId} lessonId=${lessonId}`;
      console.error(message, error);
      this.messages.error(message);
    }
  }

  private async reportEvent(
    userId: string,
    courseId: string | null,
    eventType: UserActivityEvent,
    extraData: any = {},
    tenantId?: string
  ) {
    try {
      const userActivityId = createFirestoreId(this.firestore);
      const path = `schools/${tenantId ?? this.tenant.id}/userActivityLog/${userActivityId}`;
      const collectionRef = doc(this.firestore, path);

      const data: UserActivityLog = {
        id: userActivityId,
        createdAt: Timestamp.now(),
        eventType,
        userId,
        ...(courseId ? { courseId } : {}),
        ...extraData
      };

      await setDoc(collectionRef, data);

      return data;
    } catch (error) {
      const message = `Error reporting event of type ${eventType}: courseId=${courseId} userId=${userId}`;
      console.error(message, error);
      this.messages.error(message);
    }
  }
}
